// app/frontend/pages/Users/BookmarkedEvents.tsx
import { useState, useEffect, useRef } from 'react';
import { Container, Text, Tabs, Grid, Group, Modal, Button, Loader, Center, Alert, Box } from '@mantine/core';
import { showNotification, Notifications } from '@mantine/notifications';
import FullCalendarView from '../../components/FullCalendarView';
import { IconAlertCircle, IconBook, IconCalendar, IconBookmark, IconCopy } from '@tabler/icons-react';

import { StablePagination } from '../../components/StablePagination';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import { useScrollableTabs } from '../../src/hooks/useScrollableTabs';
import '../../styles/ScrollTabs.css';

import { EventCard } from '../../components/ui';

interface UserBookmarkedEvent {
  id: number;
  name: string;
  slug: string;
  event_start_date: string;
  event_end_date: string;
  venue_name: string;
  description?: string;
  dance_style_ids?: number[];
  event_type_ids?: number[];
  event_types?: { id: number; name: string }[];
  dance_styles?: { id: number; name: string }[];
  address?: string;
  area: {
    id: number;
    name: string;
    slug: string;
    country: {
      id: number;
      name: string;
      flag_emoji: string;
    };
  };
  [key: string]: any;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  url: string;
  extendedProps: {
    flag_emoji: string;
  };
  display?: string; // Add display property for calendar styling
}

interface BookmarkedEventsProps {
  user: {
    id: number;
    username: string;
  };
}

const BookmarkedEvents = ({ user }: BookmarkedEventsProps) => {
  const [activeTab, setActiveTab] = useState<string | null>('bookmarked');
  const [bookmarkedEvents, setBookmarkedEvents] = useState<UserBookmarkedEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [unbookmarkAlert, setUnbookmarkAlert] = useState<string | null>(null);
  // Add drawer state
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<'event' | 'artist' | 'organizer'>('event');
  const [selectedEvent, setSelectedEvent] = useState<UserBookmarkedEvent | null>(null);
  const eventsPerPage = 9; // 3x3 grid

  const { tabListReference: mobileTabListReference } = useScrollableTabs();
  const { tabListReference: desktopTabListReference } = useScrollableTabs();

  const [calendarModalOpened, setCalendarModalOpened] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState<string | null>(null);
  const [lastAccessedAt, setLastAccessedAt] = useState<string | null>(null);
  const [calendarSyncEnabled, setCalendarSyncEnabled] = useState(false);

  const handleEnableCalendarSync = async () => {
    try {
      // Call the route that creates the token if needed
      const response = await fetch('/users/calendar/sync.json'); // 👈 request JSON, not redirect
      if (!response.ok) throw new Error('Failed to enable calendar sync');

      const data = await response.json();
      setCalendarFeedUrl(data.calendar_url);
      setLastAccessedAt(data.last_accessed_at); // could be null at first
      setCalendarModalOpened(true);
    } catch (err) {
      showNotification({
        title: 'Error',
        message: 'Could not enable calendar sync.',
        color: 'red',
      });
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const response = await fetch('/users/calendar/status');

      if (response.status === 422) {
        // Sync not enabled yet
        setCalendarSyncEnabled(false);
        return;
      }

      if (!response.ok) throw new Error('Unexpected error');

      const data = await response.json();
      setCalendarFeedUrl(data.calendar_url);
      setLastAccessedAt(data.last_accessed_at);
      setCalendarSyncEnabled(true); // ✅ Mark it as enabled
    } catch (err) {
      console.error('Calendar status fetch failed:', err);
    }
  };

  // Fetch bookmarked events
  const fetchBookmarkedEvents = () => {
    setLoading(true);
    fetch(`/users/${user.username}/bookmarked_events_json`)
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch bookmarked events');
        return response.json();
      })
      .then((data) => {
        console.log('Fetched bookmarked events:', data);
        setBookmarkedEvents(data);

        // Convert events to calendar format
        // Update the calendar formatting section:
        // const calendarFormatEvents = data.map((event: Event) => {
        //   // Check if it's a single-day event
        //   const startDate = new Date(event.event_start_date);
        //   const endDate = new Date(event.event_end_date);
        //   const isSingleDay = startDate.toDateString() === endDate.toDateString();

        //   return {
        //     id: event.id.toString(),
        //     // Place flag emoji before event name
        //     title: `${event.country?.flag_emoji || ''} ${event.name}`,
        //     start: event.event_start_date,
        //     end: event.event_end_date,
        //     url: `/events/${event.slug}`,
        //     // For multi-day events, we want them to span days so don't specify display
        //     // For single-day events, use 'block' to remove the dot
        //     display: isSingleDay ? 'block' : undefined,
        //     extendedProps: {
        //       flag_emoji: event.country?.flag_emoji || '',
        //     },
        //   };
        // });
        // setCalendarEvents(calendarFormatEvents);

        // Calculate total pages
        setTotalPages(Math.ceil(data.length / eventsPerPage));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching bookmarked events:', error);
        showNotification({
          title: 'Error',
          message: 'Failed to load bookmarked events',
          color: 'red',
        });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBookmarkedEvents();
  }, [user.id]);

  useEffect(() => {
    fetchBookmarkedEvents();
    fetchCalendarStatus(); // 👈 now also checks last accessed on load
  }, [user.id]);

  // Clear unbookmark alert after 5 seconds
  useEffect(() => {
    if (unbookmarkAlert) {
      const timer = setTimeout(() => {
        setUnbookmarkAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [unbookmarkAlert]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle event click - Updated to open drawer
  const handleEventClick = (event: UserBookmarkedEvent) => {
    console.log('Event data being passed to drawer:', event);
    setSelectedEvent(event);
    setDrawerContentType('event');
    setDrawerOpened(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpened(false);
  };

  // Handle calendar date set
  const handleDatesSet = () => {
    // You can add additional logic here if needed
  };

  // Calculate paginated events
  const getPaginatedEvents = () => {
    const startIndex = (currentPage - 1) * eventsPerPage;
    const endIndex = startIndex + eventsPerPage;
    return bookmarkedEvents.slice(startIndex, endIndex);
  };

  // Handle removing bookmark
  const openRemoveBookmarkModal = (event: UserBookmarkedEvent, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const confirmRemoveBookmark = () => {
    if (!selectedEvent) return;

    fetch(`/events/${selectedEvent.slug}/upvote.json`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to remove bookmark');
        return response.json();
      })
      .then((data) => {
        // Remove event from local state
        setBookmarkedEvents((prev) => prev.filter((event) => event.id !== selectedEvent.id));
        setCalendarEvents((prev) => prev.filter((event) => event.id !== selectedEvent.id.toString()));

        // Update local storage
        const bookmarkedEventsSlugs = JSON.parse(localStorage.getItem('bookmarkedEvents') || '[]');
        const updatedBookmarks = bookmarkedEventsSlugs.filter((slug: string) => slug !== selectedEvent.slug);
        localStorage.setItem('bookmarkedEvents', JSON.stringify(updatedBookmarks));

        // Show notification
        showNotification({
          title: 'Bookmark Removed',
          message: `"${selectedEvent.name}" has been removed from your bookmarks`,
          color: 'green',
          autoClose: 5000,
        });

        // Close modal
        setModalOpen(false);
        setSelectedEvent(null);

        // Recalculate total pages
        setTotalPages(Math.ceil((bookmarkedEvents.length - 1) / eventsPerPage));

        // Adjust current page if necessary
        if (currentPage > Math.ceil((bookmarkedEvents.length - 1) / eventsPerPage)) {
          setCurrentPage(Math.max(1, currentPage - 1));
        }
      })
      .catch((error) => {
        console.error('Error removing bookmark:', error);
        showNotification({
          title: 'Error',
          message: 'Failed to remove bookmark',
          color: 'red',
          autoClose: 5000,
        });
        setModalOpen(false);
      });
  };

  const cancelRemoveBookmark = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  return (
    <Container style={{ padding: '0 24px 0 24px', maxWidth: '100%' }}>
      <Notifications position="top-right" />

      <Group justify="space-between" align="top" className="us-settings-header mt-3">
        <Text size="2rem" fw={700} className="text-white" style={{ color: '#EA580C', fontSize: '1.5rem' }}>
          Your Bookmarked Events
        </Text>

        <Box>
          <Button variant="filled" color="orange" onClick={handleEnableCalendarSync}>
            📅 {calendarSyncEnabled ? 'View Calendar Sync' : 'Enable Calendar Sync'}
          </Button>

          {calendarSyncEnabled && lastAccessedAt && (
            <Text className="mt-2" size="xs" c="green">
              ✅ Sync enabled – Last synced:{' '}
              <strong>
                {new Date(lastAccessedAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
                ,{' '}
                {new Date(lastAccessedAt).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </strong>
            </Text>
          )}

          {!calendarSyncEnabled && (
            <Text className="mt-2" size="xs" c="gray">
              📅 Calendar sync is not yet enabled.
            </Text>
          )}
        </Box>

        <Modal
          opened={calendarModalOpened}
          onClose={() => setCalendarModalOpened(false)}
          title="Your Calendar Feed"
          centered
          size="lg"
          styles={{
            content: {
              backgroundColor: '#171717',
            },
            header: {
              backgroundColor: '#383838',
              borderBottom: 0,
            },
            body: {
              backgroundColor: '#171717',
            },
            title: {
              color: '#fff',
            },
          }}
        >
          {lastAccessedAt && (
            <Text className="mt-3" size="xs" c="green" mt="xs">
              ✅ Your calendar was last accessed <strong>{new Date(lastAccessedAt).toLocaleString()}</strong>
            </Text>
          )}

          <Text className="my-3" my="sm">
            Copy this link and paste it into your calendar app (e.g. Google Calendar → Add by URL).
          </Text>

          {calendarFeedUrl && (
            <Group gap="apart" mb="md">
              <Text
                size="sm"
                c="yellow"
                fw={700}
                style={{
                  backgroundColor: '#757575',
                  padding: '1.5rem',
                  borderRadius: '4px',
                  wordBreak: 'break-all',
                  flexGrow: 1,
                }}
              >
                {calendarFeedUrl}
              </Text>
              <Button
                size="sm"
                color="orange"
                leftSection={<IconCopy size={16} />}
                onClick={() => {
                  navigator.clipboard.writeText(calendarFeedUrl);
                  showNotification({
                    title: 'Copied!',
                    message: 'Calendar URL copied to clipboard',
                    color: 'green',
                  });
                }}
                style={{ marginLeft: 'auto' }}
              >
                Copy
              </Button>
            </Group>
          )}
        </Modal>
      </Group>
      <Text className="us-settings-header my-4">
        Where are you going to dance next? Use this space to organize your bookmarked events and sync them with your
        calendar with a subscription link!
      </Text>

      {/* Unbookmark Alert */}
      {unbookmarkAlert && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Event Unbookmarked"
          color="green"
          mb="md"
          withCloseButton
          onClose={() => setUnbookmarkAlert(null)}
        >
          {unbookmarkAlert}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List ref={mobileTabListReference} className="scrollable-tabs-list">
          <Tabs.Tab value="bookmarked" className="scrollable-tab" leftSection={<IconBookmark size={16} />}>
            Bookmarked Events
          </Tabs.Tab>
          <Tabs.Tab value="calendar" className="scrollable-tab" leftSection={<IconCalendar size={16} />}>
            Your Bookmarked Calendar
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="bookmarked" pt="md">
          {loading ? (
            <Center my="xl">
              <Loader color="#EA580C" size="lg" />
            </Center>
          ) : bookmarkedEvents.length > 0 ? (
            <>
              {totalPages > 1 && (
                <Group justify="flex-start" mb="md">
                  <StablePagination
                    total={totalPages}
                    value={currentPage}
                    onChange={handlePageChange}
                    color="#EA580C"
                  />
                </Group>
              )}
              <Grid mb="lg">
                {getPaginatedEvents().map((event) => (
                  <Grid.Col span={{ base: 12, sm: 6, lg: 3 }} key={event.id}>
                    <EventCard
                      event={{
                        ...event,
                        description: event.description ?? '',
                        event_type_ids: event.event_type_ids ?? [],
                        dance_style_ids: event.dance_style_ids ?? [],
                        event_types: event.event_types ?? [],
                        dance_styles: event.dance_styles ?? [],
                      }}
                      onCardClick={() => handleEventClick(event)}
                      onBookmarkClick={(e) => openRemoveBookmarkModal(event, e)}
                    />
                  </Grid.Col>
                ))}
              </Grid>
              {totalPages > 1 && (
                <Group justify="flex-start" mb="md">
                  <StablePagination
                    total={totalPages}
                    value={currentPage}
                    onChange={handlePageChange}
                    color="#EA580C"
                  />
                </Group>
              )}
            </>
          ) : (
            <Text ta="left" className="text-white" my="xl">
              You haven't bookmarked any events yet. Browse events and use the bookmark icon to save them here.
            </Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="calendar" pt="md">
          {loading ? (
            <Center my="xl">
              <Loader color="#EA580C" size="lg" />
            </Center>
          ) : (
            <FullCalendarView
              entityType="bookmarked_events"
              entityId={user.id}
              entitySlug={user.username}
              initialEvents={[]}
              currentEvents={bookmarkedEvents}
              onEventClick={handleEventClick}
              filterOptions={{
                name: false,
                danceStyles: false,
                eventTypes: false,
              }}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Confirmation Modal */}
      <Modal
        opened={modalOpen}
        onClose={cancelRemoveBookmark}
        title="Remove Bookmark"
        centered
        className="remove-bookmark-modal"
        styles={{
          content: {
            backgroundColor: '#171717',
          },
          header: {
            backgroundColor: '#383838',
            borderBottom: 0,
          },
          body: {
            backgroundColor: '#171717',
          },
          title: {
            color: '#fff',
          },
        }}
      >
        <Text className="mb-4">Are you sure you want to remove "{selectedEvent?.name}" from your bookmarks?</Text>
        <Group justify="flex-end">
          <Button variant="light" onClick={cancelRemoveBookmark}>
            Cancel
          </Button>
          <Button color="orange" onClick={confirmRemoveBookmark}>
            Remove Bookmark
          </Button>
        </Group>
      </Modal>

      {/* Event Drawer */}

      <DynamicDrawer opened={drawerOpened} type="event" data={selectedEvent} onClose={handleDrawerClose} />
    </Container>
  );
};

export default BookmarkedEvents;
